import React, { Ref, useMemo } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { RootState } from '../../../../../redux/rootState';
import { EquipmentNeededPdfComponentPropTypes } from '../../redux/type';
import './style.scss';
import taiLogo from '../../../../../assets/tailogo-eddyCurrent.png';
import { sendEquipmentType } from '../../../utils/constants';
import { sendAndReceivePdfType } from '../../utils/constants';
import { convertDateRangeToMMDDYYYY } from '../../../../../utils/CommonFunctions';

export const getPageMargins = `
 @media print {
    @page {
      margin: 30px 20px;
    }
}
`;

const EquipmentNeededPdfComponent = React.forwardRef(
  ({
    selectedRowIdWithColor, date, isChecked, checkIds,
  }: EquipmentNeededPdfComponentPropTypes, ref: Ref<HTMLDivElement>) => {
    const { typeId } = useParams();
    const { dataForPrintPdfByRequestId, sendRequestListByIdResult } = useSelector((state: RootState) => state.sendEquipmentRequestReducer);

    const selectedRowData = useMemo(() => {
      const selectedRow = sendRequestListByIdResult?.Data?.find((ele) => String(ele?.RequestId) === String(selectedRowIdWithColor));
      return selectedRow;
    }, [dataForPrintPdfByRequestId?.Data]);

    const getHeader = useMemo(() => {
      if (Number(typeId) === sendEquipmentType.SEND_EQUIPMENT || isChecked) {
        return sendAndReceivePdfType.SEND_HEADER;
      }
      return sendAndReceivePdfType.RECEIVE_HEADER;
    }, [typeId, isChecked]);

    const getShippingDate = useMemo(() => {
      if (Number(typeId) === sendEquipmentType.SEND_EQUIPMENT || isChecked) {
        return `Shipped Date: ${date ? dayjs(date).format('MM-DD-YYYY') : ''}`;
      }
      return `Received Date: ${date ? dayjs(date).format('MM-DD-YYYY') : ''}`;
    }, [typeId, selectedRowData, isChecked, date]);

    const displayData = useMemo(() => {
      if (dataForPrintPdfByRequestId?.Data?.length > 0) {
        const data = dataForPrintPdfByRequestId?.Data?.map((item) => ({
          ...item,
          isChecked: checkIds.includes(item.ProductNumber),
        }));
        return isChecked ? data?.filter((item) => item?.isChecked) : data;
      }
      return [];
    }, [dataForPrintPdfByRequestId]);

    return (
      <div ref={ref} className="tableContainerContent">
        <style>{getPageMargins}</style>
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}
        >
          <div className="ms-3">
            <img src={taiLogo} style={{ width: '200px', height: 'auto' }} alt="" />
          </div>
          <div>
            <div className="firstText me-3">1000 Cobb Place Boulevard NW Bldg 300 Suite 300</div>
            <div className="firstText me-3">300 Kennesaw GA 30144</div>
            <div className="firstText me-3">(770)763-5930 | (800)554-4127</div>
          </div>
        </div>
        <div className="shippingListClass">{getHeader}</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <div>{`Job Number: ${selectedRowData?.JobNumber}`}</div>
            <div>{`Analyst: ${selectedRowData?.Analyst}`}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
            <div>{getShippingDate}</div>
            <div>{`Job Dates: ${selectedRowData?.JobDates ? convertDateRangeToMMDDYYYY(selectedRowData?.JobDates) : ''}`}</div>
          </div>
        </div>
        <TableContainer sx={{ marginTop: '20px' }}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow className="tableRowClass">
                <TableCell>Product #</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData?.length > 0 && displayData?.map((row) => (
                <TableRow
                  key={row?.ProductNumber}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">{row?.ProductNumber}</TableCell>
                  <TableCell>{row?.Description}</TableCell>
                  <TableCell>{row?.Location}</TableCell>
                  <TableCell>1</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {dataForPrintPdfByRequestId?.Instructions?.trim()?.length > 0 ? (
          <>
            <div className="instructionClass instructionHeading">
              Instructions :
            </div>
            <div className="instructionClass mt-0 pt-0">
              <div className="instructionClassSpan customBreakWords">{dataForPrintPdfByRequestId?.Instructions}</div>
            </div>
          </>
        ) : (
          <div className="instructionClass instructionHeading" />
        )}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '45px',
            marginTop: '50px',
            marginBottom: '8px',
          }}
          className="bottomWrapper"
        >
          <div className="bottomTextClass">Nondestructive Inspections</div>
          <div className="bottomTextClass">Eddy Current | Flux Leakage | Oil Analysis</div>
        </div>
      </div>
    );
  },
);

export default EquipmentNeededPdfComponent;
